import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountTypeChangeInput,
  AccountTypeChangeResultDto,
  AccountTypeScreenDto,
  ActivityDto,
  AddProductResultDto,
  AddressUpdateInput,
  ApiLogDto,
  CustomerDto,
  CustomerFlagsInput,
  CustomerInfoInput,
  CustomerListItemDto,
  CustomerOrderDto,
  DidNumberInput,
  IvrPinInput,
  NoteCreateInput,
  NoteDto,
  NotePriorityInput,
  Paged,
  SimChangeInput,
  SimChangeResultDto,
  StatusChangeInput,
  UserRef,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/* ---------------------------- Search and history --------------------------- */

/** Customer search (PC56). Runs only when there is at least one search value. */
export const useCustomerSearch = (params: Query, enabled: boolean) =>
  useQuery({
    queryKey: ['customers', 'search', params],
    queryFn: () => api.get<Paged<CustomerListItemDto>>('/customers', params),
    placeholderData: (prev) => prev,
    enabled,
  });

/** "CSR Search History" (PC295): the customers this user opened. */
export const useRecentSearches = (params: Query) =>
  useQuery({
    queryKey: ['customers', 'recent', params],
    queryFn: () => api.get<Paged<CustomerListItemDto>>('/customers/recent', params),
    placeholderData: (prev) => prev,
  });

/* --------------------------------- Profile -------------------------------- */

export const useCustomer = (id: string | undefined) =>
  useQuery({
    queryKey: ['customers', 'detail', id],
    queryFn: () => api.get<CustomerDto>(`/customers/${id}`),
    enabled: Boolean(id),
  });

export const useCustomerNotes = (id: string, params: Query) =>
  useQuery({
    queryKey: ['customers', 'notes', id, params],
    queryFn: () => api.get<Paged<NoteDto>>(`/customers/${id}/notes`, params),
    placeholderData: (prev) => prev,
  });

/** The carrier API log is loaded when its panel is opened. */
export const useCustomerApiLog = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['customers', 'api-log', id],
    queryFn: () => api.get<{ data: ApiLogDto[] }>(`/customers/${id}/api-log`),
    select: (r) => r.data,
    enabled,
  });

export const useCustomerOrders = (id: string) =>
  useQuery({
    queryKey: ['customers', 'orders', id],
    queryFn: () => api.get<{ data: CustomerOrderDto[] }>(`/customers/${id}/orders`),
    select: (r) => r.data,
  });

export const useCustomerActivity = (id: string, params: Query) =>
  useQuery({
    queryKey: ['customers', 'activity', id, params],
    queryFn: () => api.get<Paged<ActivityDto>>(`/customers/${id}/activity`, params),
    placeholderData: (prev) => prev,
  });

/* --------------------------------- Changes -------------------------------- */

/**
 * Every change to a customer shows up in several places (profile, lists, notes, activity
 * log, API log), so all customer queries are refreshed after one.
 */
function useCustomerMutation<TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  alsoInvalidate: string[] = [],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all(
        ['customers', ...alsoInvalidate].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      ),
  });
}

export const useAddNote = (id: string) =>
  useCustomerMutation((input: NoteCreateInput) =>
    api.post<NoteDto>(`/customers/${id}/notes`, input),
  );

/** Mark Void (PC248): archives one note. */
export const useVoidNote = (id: string) =>
  useCustomerMutation((noteId: string) =>
    api.post<NoteDto>(`/customers/${id}/notes/${noteId}/void`),
  );

/** The users who posted notes for the customer: the Display Notes "Posted By" choices. */
export const useNotePosters = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['customers', 'notes', id, 'posted-by'],
    queryFn: () => api.get<{ data: UserRef[] }>(`/customers/${id}/notes/posted-by`),
    select: (r) => r.data,
    enabled,
  });

/** Verify Email: sends the customer a verification e-mail. */
export const useVerifyEmail = (id: string) =>
  useCustomerMutation(() => api.post<{ message: string }>(`/customers/${id}/verify-email`));

/* ------------------- Change Account Type, Add Product ---------------------- */

export const useAccountTypeScreen = (id: string) =>
  useQuery({
    queryKey: ['customers', id, 'account-type'],
    queryFn: () => api.get<AccountTypeScreenDto>(`/customers/${id}/account-type`),
  });

export const useChangeAccountType = (id: string) =>
  useCustomerMutation((input: AccountTypeChangeInput) =>
    api.post<AccountTypeChangeResultDto>(`/customers/${id}/account-type`, input),
  );

export const useAddProduct = (id: string) =>
  useCustomerMutation(() => api.post<AddProductResultDto>(`/customers/${id}/add-product`));

export const useUpdateFlags = (id: string) =>
  useCustomerMutation((input: CustomerFlagsInput) =>
    api.patch<CustomerDto>(`/customers/${id}/flags`, input),
  );

export const useUpdateDid = (id: string) =>
  useCustomerMutation((input: DidNumberInput) =>
    api.put<CustomerDto>(`/customers/${id}/did`, input),
  );

export const useUpdateIvrPin = (id: string) =>
  useCustomerMutation((input: IvrPinInput) =>
    api.put<CustomerDto>(`/customers/${id}/ivr-pin`, input),
  );

export const useSetNotePriority = (id: string) =>
  useCustomerMutation(({ noteId, ...input }: NotePriorityInput & { noteId: string }) =>
    api.patch<NoteDto>(`/customers/${id}/notes/${noteId}/priority`, input),
  );

/** "Cancel AutoPay" on the profile: the Auto Pay switch turned off. */
export const useCancelAutoPay = (id: string) =>
  useCustomerMutation(() => api.put(`/customers/${id}/autopay`, { enabled: false }));

export const useSetPortoutLock = (id: string) =>
  useCustomerMutation((locked: boolean) =>
    api.put<CustomerDto>(`/customers/${id}/portout-lock`, { locked }),
  );

export const useAddRetentionNote = (id: string) =>
  useCustomerMutation((text: string) =>
    api.post<NoteDto>(`/customers/${id}/retention-note`, { text }),
  );

export const useSendBalanceSms = (id: string) =>
  useCustomerMutation(() => api.post<{ message: string }>(`/customers/${id}/balance-sms`));

export type Preferences = CustomerDto['preferences'];
export const useUpdatePreferences = (id: string) =>
  useCustomerMutation((input: Preferences) =>
    api.patch<CustomerDto>(`/customers/${id}/preferences`, input),
  );

export const useChangeStatus = (id: string) =>
  useCustomerMutation((input: StatusChangeInput) =>
    api.post<CustomerDto>(`/customers/${id}/status`, input),
  );

export const useChangeAddress = (id: string) =>
  useCustomerMutation((input: AddressUpdateInput) =>
    api.patch<CustomerDto>(`/customers/${id}/address`, input),
  );

export const useUpdateInfo = (id: string) =>
  useCustomerMutation((input: CustomerInfoInput) =>
    api.patch<CustomerDto>(`/customers/${id}/info`, input),
  );

export const useUpdateTags = (id: string) =>
  useCustomerMutation((input: { tags: string[] }) =>
    api.put<CustomerDto>(`/customers/${id}/tags`, input),
  );

/** A SIM change moves two inventory units as well. */
export const useChangeSim = (id: string) =>
  useCustomerMutation(
    (input: SimChangeInput) => api.post<SimChangeResultDto>(`/customers/${id}/sim-change`, input),
    ['inventory'],
  );
